import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  BookOpenCheck,
  CheckCircle2,
  ClipboardCheck,
  Clock3,
  PlayCircle,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';
import { examApi } from '../../services/exam.service';
import { ExamAttempt, Question } from '../../types/exam';
import api from '../../services/api';

type TeacherProfile = {
  id: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
};

const toErrorMessage = (error: any, fallback: string): string => {
  const payload = error?.response?.data?.message ?? error?.message;
  if (Array.isArray(payload)) {
    return payload.map((item) => String(item)).join(', ');
  }
  if (payload && typeof payload === 'object') {
    const nestedMessage = (payload as any).message;
    if (Array.isArray(nestedMessage)) {
      return nestedMessage.map((item: any) => String(item)).join(', ');
    }
    if (typeof nestedMessage === 'string') {
      return nestedMessage;
    }
    return (payload as any).error || fallback;
  }
  if (typeof payload === 'string' && payload.trim()) {
    return payload;
  }
  return fallback;
};

const TeacherCertificationExam: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [attempt, setAttempt] = useState<ExamAttempt | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [profile, setProfile] = useState<TeacherProfile | null>(null);
  const [status, setStatus] = useState<any>(null);
  const [error, setError] = useState<string>('');
  const [showAttemptQuestions, setShowAttemptQuestions] = useState(false);

  const loadAttemptQuestions = async (attemptId: string) => {
    const questionList = await examApi.getTeacherCertificationQuestions(attemptId);
    setQuestions(Array.isArray(questionList) ? questionList : []);
  };

  const loadPage = async () => {
    try {
      setLoading(true);
      const [profileRes, certificationStatus] = await Promise.all([
        api.get<TeacherProfile>('/teachers/profile'),
        examApi.getTeacherCertificationStatus(),
      ]);
      setProfile(profileRes.data || null);
      setStatus(certificationStatus);

      if (certificationStatus?.latestAttempt?.attemptId) {
        setAttempt(certificationStatus.latestAttempt);
        setQuestions([]);
        setAnswers({});
        setShowAttemptQuestions(false);
      } else {
        setAttempt(null);
        setQuestions([]);
        setAnswers({});
        setShowAttemptQuestions(false);
      }
      setError('');
    } catch (err: any) {
      setError(toErrorMessage(err, 'Failed to load certification exam'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadPage();
  }, []);

  const hasPassed = Boolean(status?.isPassed);
  const isApproved = profile?.status === 'APPROVED';
  const isRejected = profile?.status === 'REJECTED';
  const inProgressAttempt = attempt?.status === 'IN_PROGRESS';

  const totalAnswered = useMemo(
    () =>
      questions.reduce((count, question) => {
        const value = answers[question.questionId];
        return value ? count + 1 : count;
      }, 0),
    [answers, questions],
  );

  const completionPercentage = questions.length ? Math.round((totalAnswered / questions.length) * 100) : 0;
  const unansweredCount = Math.max(questions.length - totalAnswered, 0);

  const startAttempt = async () => {
    try {
      setLoading(true);
      const startedAttempt = await examApi.startTeacherCertificationAttempt();
      setAttempt(startedAttempt);
      setAnswers({});
      await loadAttemptQuestions(startedAttempt.attemptId);
      setShowAttemptQuestions(true);
      setError('');
    } catch (err: any) {
      setError(toErrorMessage(err, 'Failed to start exam'));
    } finally {
      setLoading(false);
    }
  };

  const resumeAttemptNow = async () => {
    if (!attempt?.attemptId) return;
    try {
      setLoading(true);
      await loadAttemptQuestions(attempt.attemptId);
      setShowAttemptQuestions(true);
      setError('');
    } catch (err: any) {
      setError(toErrorMessage(err, 'Failed to load in-progress attempt'));
    } finally {
      setLoading(false);
    }
  };

  const onSelectAnswer = async (questionId: string, value: string) => {
    if (!attempt?.attemptId) return;
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
    try {
      await examApi.saveTeacherCertificationAnswer(attempt.attemptId, {
        questionId,
        answer: value,
        timeSpent: 0,
      });
    } catch (err) {
      console.error('Failed to save answer:', err);
    }
  };

  const submitAttempt = async () => {
    if (!attempt?.attemptId || questions.length === 0) return;

    const unanswered = questions.filter((q) => !answers[q.questionId]);
    if (unanswered.length > 0) {
      alert(`Please answer all questions before submit. Remaining: ${unanswered.length}`);
      return;
    }

    try {
      setSubmitting(true);
      await examApi.submitTeacherCertificationAttempt(attempt.attemptId, { totalTimeSpent: 0 });
      await loadPage();
      alert('Certification exam submitted.');
    } catch (err: any) {
      alert(toErrorMessage(err, 'Failed to submit exam'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white/90 px-6 py-16 shadow-[0_24px_80px_-48px_rgba(15,23,42,0.45)]">
        <div className="mx-auto max-w-md text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-950 text-white">
            <ClipboardCheck className="h-8 w-8" />
          </div>
          <p className="mt-5 text-lg font-semibold text-slate-950">Loading teacher eligibility test</p>
          <p className="mt-2 text-sm text-slate-500">Preparing your certification status and attempt details.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-4">
      <section className="overflow-hidden rounded-[2rem] border border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(34,197,94,0.15),_transparent_24%),linear-gradient(135deg,_#020617_0%,_#0f172a_52%,_#14532d_100%)] text-white shadow-[0_30px_90px_-48px_rgba(15,23,42,0.65)]">
        <div className="grid gap-6 px-5 py-6 sm:px-7 lg:grid-cols-[minmax(0,1.6fr)_minmax(280px,0.9fr)] lg:px-8 lg:py-8">
          <div className="space-y-5">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-emerald-100">
              <Sparkles className="h-4 w-4" />
              Teacher Eligibility Test
            </div>
            <div className="space-y-3">
              <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">
                Complete certification before teacher approval goes live.
              </h1>
              <p className="max-w-2xl text-sm leading-6 text-slate-200 sm:text-base">
                This test verifies teaching readiness, classroom judgment, and subject handling before your profile can move into approved status.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => navigate('/teacher/dashboard')}
                className="inline-flex items-center justify-center rounded-full bg-white px-5 py-3 text-sm font-semibold text-slate-950 transition hover:bg-slate-100"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Dashboard
              </button>
              {!hasPassed && !inProgressAttempt && (
                <button
                  type="button"
                  onClick={startAttempt}
                  className="inline-flex items-center justify-center rounded-full border border-emerald-300/35 bg-emerald-400/10 px-5 py-3 text-sm font-semibold text-emerald-50 transition hover:bg-emerald-400/20"
                >
                  <PlayCircle className="mr-2 h-4 w-4" />
                  Start Eligibility Test
                </button>
              )}
              {inProgressAttempt && !showAttemptQuestions && (
                <button
                  type="button"
                  onClick={resumeAttemptNow}
                  className="inline-flex items-center justify-center rounded-full border border-sky-300/35 bg-sky-400/10 px-5 py-3 text-sm font-semibold text-sky-50 transition hover:bg-sky-400/20"
                >
                  Resume Attempt
                </button>
              )}
            </div>
          </div>

          <div className="grid gap-3">
            <div className="rounded-[1.75rem] border border-white/12 bg-white/10 p-5 backdrop-blur">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-300">Passing score</p>
              <p className="mt-3 text-4xl font-semibold text-white">
                {status?.passingPercentage ? `${Number(status.passingPercentage).toFixed(0)}%` : '--'}
              </p>
              <p className="mt-2 text-xs text-slate-300">Minimum score required to qualify for admin approval.</p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
              <div className="rounded-2xl border border-white/12 bg-slate-950/20 p-4 backdrop-blur">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10">
                    <BookOpenCheck className="h-5 w-5 text-emerald-100" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">{status?.requiredQuestionCount || 10} questions</p>
                    <p className="text-xs text-slate-300">Randomized from the active teacher question bank.</p>
                  </div>
                </div>
              </div>
              <div className="rounded-2xl border border-white/12 bg-slate-950/20 p-4 backdrop-blur">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10">
                    <ShieldCheck className="h-5 w-5 text-emerald-100" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">
                      {hasPassed ? 'Passed' : inProgressAttempt ? 'In Progress' : 'Not Attempted'}
                    </p>
                    <p className="text-xs text-slate-300">Current certification state tied to your teacher profile.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {error && (
        <div className="rounded-[1.5rem] border border-rose-200 bg-rose-50 px-5 py-4 text-sm text-rose-700">
          {error}
        </div>
      )}

      {isApproved && (
        <div className="rounded-[1.5rem] border border-emerald-200 bg-emerald-50 px-5 py-4 text-sm text-emerald-800">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 h-5 w-5" />
              <div>
                <p className="font-semibold">Teacher account approved</p>
                <p className="mt-1">Your eligibility test and approval are complete. You can continue to your dashboard.</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => navigate('/teacher/dashboard')}
              className="inline-flex items-center rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700"
            >
              Go To Dashboard
            </button>
          </div>
        </div>
      )}

      {isRejected && (
        <div className="rounded-[1.5rem] border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-800">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5" />
            <div>
              <p className="font-semibold">Profile rejected</p>
              <p className="mt-1">Your teacher profile was rejected by admin. Update your profile and retake certification if required.</p>
            </div>
          </div>
        </div>
      )}

      {hasPassed && !isApproved && (
        <div className="rounded-[1.5rem] border border-sky-200 bg-sky-50 px-5 py-4 text-sm text-sky-800">
          <div className="flex items-start gap-3">
            <ShieldCheck className="mt-0.5 h-5 w-5" />
            <div>
              <p className="font-semibold">Eligibility test passed</p>
              <p className="mt-1">You already passed the certification exam. Admin approval is the next step.</p>
            </div>
          </div>
        </div>
      )}

      {!hasPassed && !inProgressAttempt && (
        <div className="grid gap-6 xl:grid-cols-[minmax(0,1.15fr)_340px]">
          <div className="overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white shadow-[0_24px_80px_-48px_rgba(15,23,42,0.4)]">
            <div className="border-b border-slate-200 px-5 py-5 sm:px-6">
              <h2 className="text-xl font-semibold tracking-tight text-slate-950">Before you begin</h2>
              <p className="mt-1 text-sm text-slate-500">The test is straightforward, but it should be treated like an approval checkpoint.</p>
            </div>
            <div className="grid gap-4 px-5 py-5 sm:px-6">
              <div className="rounded-[1.25rem] border border-slate-200 bg-slate-50 p-4">
                <p className="text-sm font-semibold text-slate-950">Question mix</p>
                <p className="mt-1 text-sm text-slate-500">You will get a randomized set of pedagogy and classroom-readiness questions.</p>
              </div>
              <div className="rounded-[1.25rem] border border-slate-200 bg-slate-50 p-4">
                <p className="text-sm font-semibold text-slate-950">Submission rule</p>
                <p className="mt-1 text-sm text-slate-500">All questions must be answered before submission is accepted.</p>
              </div>
              <div className="rounded-[1.25rem] border border-slate-200 bg-slate-50 p-4">
                <p className="text-sm font-semibold text-slate-950">Approval path</p>
                <p className="mt-1 text-sm text-slate-500">Passing the test is required before admin can approve your teacher account.</p>
              </div>
            </div>
          </div>

          <div className="overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white shadow-[0_24px_80px_-48px_rgba(15,23,42,0.4)]">
            <div className="border-b border-slate-200 px-5 py-5 sm:px-6">
              <h3 className="text-xl font-semibold tracking-tight text-slate-950">Start Attempt</h3>
              <p className="mt-1 text-sm text-slate-500">Ready when you are.</p>
            </div>
            <div className="space-y-4 px-5 py-5 sm:px-6">
              <div className="rounded-[1.25rem] bg-slate-950 p-5 text-white">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-300">Exam snapshot</p>
                <p className="mt-3 text-3xl font-semibold">{status?.requiredQuestionCount || 10}</p>
                <p className="mt-2 text-sm text-slate-300">Questions in this attempt</p>
              </div>
              <button
                type="button"
                onClick={startAttempt}
                className="inline-flex w-full items-center justify-center rounded-full bg-emerald-600 px-5 py-3.5 text-sm font-semibold text-white transition hover:bg-emerald-700"
              >
                Attempt Now
              </button>
              <button
                type="button"
                onClick={() => navigate('/teacher/dashboard')}
                className="inline-flex w-full items-center justify-center rounded-full border border-slate-200 bg-white px-5 py-3.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                Attempt Later
              </button>
            </div>
          </div>
        </div>
      )}

      {inProgressAttempt && !showAttemptQuestions && (
        <div className="overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white shadow-[0_24px_80px_-48px_rgba(15,23,42,0.4)]">
          <div className="border-b border-slate-200 px-5 py-5 sm:px-6">
            <h2 className="text-xl font-semibold tracking-tight text-slate-950">Attempt waiting to resume</h2>
            <p className="mt-1 text-sm text-slate-500">Your previous in-progress eligibility test is ready to continue.</p>
          </div>
          <div className="flex flex-wrap gap-3 px-5 py-5 sm:px-6">
            <button
              type="button"
              onClick={resumeAttemptNow}
              className="inline-flex items-center rounded-full bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
            >
              Resume Attempt Now
            </button>
            <button
              type="button"
              onClick={() => navigate('/teacher/dashboard')}
              className="inline-flex items-center rounded-full border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              Later
            </button>
          </div>
        </div>
      )}

      {inProgressAttempt && showAttemptQuestions && (
        <div className="grid gap-6 xl:grid-cols-[minmax(0,1.15fr)_320px]">
          <div className="space-y-4">
            {questions.map((question, index) => (
              <div
                key={question.questionId}
                className="overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white shadow-[0_24px_80px_-48px_rgba(15,23,42,0.4)]"
              >
                <div className="border-b border-slate-200 px-5 py-4 sm:px-6">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-400">Question {index + 1}</p>
                    <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                      {answers[question.questionId] ? 'Answered' : 'Pending'}
                    </span>
                  </div>
                  <p className="mt-3 text-lg font-semibold leading-7 text-slate-950">{question.questionText}</p>
                </div>
                <div className="grid gap-3 px-5 py-5 sm:px-6">
                  {(question.options || []).map((option) => {
                    const active = answers[question.questionId] === option.text;
                    return (
                      <label
                        key={`${question.questionId}-${option.text}`}
                        className={`flex cursor-pointer items-start gap-3 rounded-[1.25rem] border px-4 py-3 text-sm transition ${
                          active
                            ? 'border-emerald-500 bg-emerald-50 text-emerald-900'
                            : 'border-slate-200 bg-slate-50 text-slate-700 hover:border-slate-300 hover:bg-white'
                        }`}
                      >
                        <input
                          type="radio"
                          name={question.questionId}
                          checked={active}
                          onChange={() => onSelectAnswer(question.questionId, option.text)}
                          className="mt-1"
                        />
                        <span>{option.text}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <aside className="space-y-5">
            <div className="overflow-hidden rounded-[1.75rem] border border-slate-200 bg-slate-950 text-white shadow-[0_24px_80px_-48px_rgba(15,23,42,0.55)]">
              <div className="px-5 py-5 sm:px-6">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-300">Attempt Progress</p>
                <p className="mt-3 text-3xl font-semibold">{completionPercentage}%</p>
                <div className="mt-4 h-2.5 rounded-full bg-white/10">
                  <div
                    className="h-2.5 rounded-full bg-emerald-400 transition-all"
                    style={{ width: `${completionPercentage}%` }}
                  />
                </div>
                <div className="mt-5 grid gap-3">
                  <div className="rounded-2xl bg-white/5 px-4 py-3">
                    <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Answered</p>
                    <p className="mt-1 text-sm font-medium text-white">{totalAnswered} of {questions.length}</p>
                  </div>
                  <div className="rounded-2xl bg-white/5 px-4 py-3">
                    <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Remaining</p>
                    <p className="mt-1 text-sm font-medium text-white">{unansweredCount}</p>
                  </div>
                  <div className="rounded-2xl bg-white/5 px-4 py-3">
                    <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Passing score</p>
                    <p className="mt-1 text-sm font-medium text-white">{Number(status?.passingPercentage || 0).toFixed(0)}%</p>
                  </div>
                </div>
                <button
                  type="button"
                  disabled={submitting || questions.length === 0}
                  onClick={submitAttempt}
                  className="mt-6 inline-flex w-full items-center justify-center rounded-full bg-emerald-500 px-5 py-3.5 text-sm font-semibold text-slate-950 transition hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {submitting ? 'Submitting...' : 'Submit Eligibility Test'}
                </button>
              </div>
            </div>

            <div className="overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white shadow-[0_24px_80px_-48px_rgba(15,23,42,0.4)]">
              <div className="px-5 py-5 sm:px-6">
                <p className="text-base font-semibold text-slate-950">Attempt guidance</p>
                <div className="mt-4 space-y-3 text-sm text-slate-600">
                  <div className="flex items-start gap-3">
                    <Clock3 className="mt-0.5 h-4 w-4 text-slate-400" />
                    <p>Answer every question before submitting. Partial completion cannot be submitted.</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <ClipboardCheck className="mt-0.5 h-4 w-4 text-slate-400" />
                    <p>Your answers are saved as you select them, so you can resume an interrupted attempt.</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <ShieldCheck className="mt-0.5 h-4 w-4 text-slate-400" />
                    <p>Passing this test is required for eligibility, but admin approval still controls final activation.</p>
                  </div>
                </div>
              </div>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
};

export default TeacherCertificationExam;
